import type { ZodTypeAny } from "zod"

type ExternalBoundaryValidationSeverity = "warn" | "handled-error" | "throw"

interface ExternalBoundaryIssue {
  code: string
  message: string
  path: string
}

interface WarnLogger {
  warn: (message: string, data?: Record<string, unknown>) => void
  error?: (
    message: string,
    error?: unknown,
    data?: Record<string, unknown>,
  ) => void
}

interface WarnOnInvalidExternalDataOptions {
  boundary: string
  log: WarnLogger
  data?: Record<string, unknown>
  severity?: ExternalBoundaryValidationSeverity
}

const summarizeIssuePath = (path: Array<string | number>): string =>
  path.length > 0 ? path.join(".") : "<root>"

class ExternalBoundaryValidationError extends Error {
  readonly boundary: string
  readonly issues: ExternalBoundaryIssue[]

  constructor(boundary: string, issues: ExternalBoundaryIssue[]) {
    super(`External boundary shape mismatch: ${boundary}`)
    this.name = "ExternalBoundaryValidationError"
    this.boundary = boundary
    this.issues = issues
  }
}

export function isExternalBoundaryValidationError(
  error: unknown,
): error is Error {
  return error instanceof ExternalBoundaryValidationError
}

export function warnOnInvalidExternalData<T>(
  value: T,
  schema: ZodTypeAny,
  { boundary, log, data, severity = "warn" }: WarnOnInvalidExternalDataOptions,
): T {
  const result = schema.safeParse(value)
  if (!result.success) {
    const issues = result.error.issues.map((issue) => ({
      code: issue.code,
      message: issue.message,
      path: summarizeIssuePath(issue.path),
    }))
    const message = `External boundary shape mismatch: ${boundary}`
    const context = {
      ...data,
      issues,
      severity,
    }

    const error = new ExternalBoundaryValidationError(boundary, issues)

    if (severity === "throw") {
      if (log.error) {
        log.error(message, error, {
          ...context,
          enforced: true,
        })
      } else {
        log.warn(message, context)
      }
      throw error
    }

    if (severity === "handled-error") {
      log.warn(message, {
        ...context,
        handled: true,
      })
    } else {
      log.warn(message, context)
    }
  }

  return value
}
